/**
 * The social half of an event page: who's coming, and photos from the night.
 *
 * Both were already modelled in the database and shown to nobody.
 */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { api, apiUrl, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { toast } from '../lib/toast';
import { Avatar } from './Avatar';
import { Badge, Button, useConfirm } from './ui';

interface Attendee {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  guestsCount: number;
  avatarUrl: string | null;
}

interface Photo {
  id: string;
  url: string;
  caption: string | null;
  status: string;
  uploaderName: string | null;
  mine: boolean;
}

const mediaSrc = (url: string) => apiUrl(url.replace(/^\/api\/v\d+/, ''));

/** Who's coming — faces, not a headcount. */
export function Attendees({ eventId }: { eventId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['attendees', eventId],
    queryFn: () =>
      api.get<{ data: Attendee[] }>(`/events/${eventId}/whos-coming`).then((r) => r.data),
  });

  if (isLoading) return <p className="text-sm text-cream-300/60">Loading…</p>;

  const going = (data ?? []).filter((a) => a.status === 'registered');
  const waiting = (data ?? []).filter((a) => a.status === 'waitlist');
  const guests = going.reduce((n, a) => n + a.guestsCount, 0);

  if (!going.length && !waiting.length) {
    return (
      <p className="text-sm text-cream-300/50">
        Nobody has RSVP'd yet. Be the first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs uppercase tracking-wide text-cream-300/45">
          Coming ({going.length}
          {guests > 0 ? ` + ${guests} guest${guests === 1 ? '' : 's'}` : ''})
        </p>
        <ul className="flex flex-wrap gap-3">
          {going.map((a) => (
            <li key={a.id} className="flex items-center gap-2">
              <Avatar
                firstName={a.firstName}
                lastName={a.lastName}
                avatarUrl={a.avatarUrl}
                size="sm"
              />
              <span className="text-sm text-cream-200">
                {a.firstName} {a.lastName[0]}.
                {a.guestsCount > 0 ? (
                  <span className="text-cream-300/50"> +{a.guestsCount}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {waiting.length ? (
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-cream-300/45">
            Waitlist ({waiting.length})
          </p>
          <ul className="flex flex-wrap gap-2">
            {waiting.map((a) => (
              <li key={a.id}>
                <Avatar
                  firstName={a.firstName}
                  lastName={a.lastName}
                  avatarUrl={a.avatarUrl}
                  size="sm"
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Photos from the night, with a moderation queue for records admins. */
export function Gallery({ eventId }: { eventId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const moderator = user ? can(user.roles, 'records:approve') : false;

  const { data, isLoading } = useQuery({
    queryKey: ['gallery', eventId],
    queryFn: () =>
      api
        .get<{ data: Photo[] }>(`/media?eventId=${eventId}&type=photo`)
        .then((r) => r.data),
  });

  const moderate = useMutation({
    mutationFn: (vars: { id: string; status: 'approved' | 'rejected' }) =>
      api.patch(`/media/${vars.id}`, { status: vars.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery', eventId] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/media/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gallery', eventId] }),
  });

  async function upload(file: File) {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('type', 'photo');
      form.append('eventId', eventId);
      form.append('file', file);
      const result = await api.upload<{ status: string }>('/media', form);
      toast.success(
        result.status === 'pending'
          ? 'Photo submitted — it appears once a records admin approves it.'
          : 'Photo added.',
      );
      void qc.invalidateQueries({ queryKey: ['gallery', eventId] });
    } catch (e) {
      toast.error(
        e instanceof ApiError ? e.message : 'That photo could not be uploaded.',
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const photos = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-cream-300/70">
          Anyone may add one; a records admin publishes it.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
          }}
        />
        <Button
          variant="ghost"
          loading={uploading}
          className="shrink-0 whitespace-nowrap"
          onClick={() => fileRef.current?.click()}
        >
          Add a photo
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : !photos.length ? (
        <p className="text-sm text-cream-300/50">No photos yet.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((p) => (
            <li key={p.id} className="group relative overflow-hidden rounded-lg border border-ink-800">
              <img
                src={mediaSrc(p.url)}
                alt={p.caption ?? ''}
                loading="lazy"
                className="aspect-square w-full object-cover"
              />

              {p.status !== 'approved' ? (
                <span className="absolute left-2 top-2">
                  <Badge tone={p.status === 'pending' ? 'warning' : 'danger'}>{p.status}</Badge>
                </span>
              ) : null}

              <div className="absolute inset-x-0 bottom-0 bg-ink-950/80 p-2 opacity-0 transition group-hover:opacity-100">
                <p className="truncate text-[11px] text-cream-300/70">
                  {p.uploaderName ?? 'A member'}
                </p>
                <div className="mt-1 flex gap-2">
                  {moderator && p.status === 'pending' ? (
                    <>
                      <button
                        className="text-[11px] text-success hover:underline"
                        onClick={() => moderate.mutate({ id: p.id, status: 'approved' })}
                      >
                        Approve
                      </button>
                      <button
                        className="text-[11px] text-danger hover:underline"
                        onClick={() => moderate.mutate({ id: p.id, status: 'rejected' })}
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                  {(p.mine || moderator) && (
                    <button
                      className="text-[11px] text-cream-300/60 hover:text-danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Remove this photo?',
                          confirmLabel: 'Remove',
                          destructive: true,
                        });
                        if (ok) remove.mutate(p.id);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
